import { CustomerVerificationStatus } from '@lambda/apis/src/customer/enum';

export interface SmartformsAttachment {
  contentType: string;
  fileName: string;
  bucketUrl: string;
}

export interface ShipmentLineItemBase {
  name: string;
  vendor: string;
  id: string;
}

export type ShipmentBase = {
  lineItems: Array<ShipmentLineItemBase>;
};

export interface CustomerOrderBase {
  orderName: string;
  orderNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  processedAt: string;
  shipments: Array<ShipmentBase>;
  shopifyOrderId?: string | number;
}

export type SmartFormType = 'return' | 'help' | 'warranty' | 'cancellation';

export type ShippingAddressDetailProps = {
  firstName?: string;
  lastName?: string;
  address1: string;
  address2?: string;
  city: string;
  country: string;
  province: string;
  zip: string;
};

type DidNotReceiveOrderReason = 'DNR-1' | 'DNR-2';

export const DidNotReceiveOrderReasonMapping: Record<
  DidNotReceiveOrderReason,
  string
> = {
  'DNR-1': 'Tracking delivery in progress or lost',
  'DNR-2': 'Tracking shows delivered but not received',
};

export type SmartFormParams = {
  whatHappened?: string;
  nextStep?: string;
  reasonForReturn?: string;
  reasonForChangeOfMind?: string;
  selectedHelpOption?: string;
  textData?: string;
  files?: string[];
  productName?: string;
  newShippingAddress?: ShippingAddressDetailProps | null;
  newPhone?: string | null;
  newEmail?: string | null;
  IMEI?: string | null;
  lineItemId?: string | null;
  vendorName?: string | null;
  // tracking
  deliveredDate?: string | null;
  dispatchedDate?: string | null;
  trackingStatus?:
    | 'Pending'
    | 'InfoReceived'
    | 'InTransit'
    | 'OutForDelivery'
    | 'AttemptFail'
    | 'Delivered'
    | 'AvailableForPickup'
    | 'Exception'
    | 'Expired'
    | null;
  outOfDeliveryDate?: string | null;
  warrantyType?: 'reebelocare' | 'standard' | null;
  warrantyEndDate?: string | null;
  incidentDate?: string | null;
  imagesUrl?: string;
  customTag?: string;
  postShipmentCancellation?: boolean;
  shouldCreateInstantReturnLabel?: boolean;
  customerVerificationStatus?: CustomerVerificationStatus | '';
  didNotReceiveOrderReason?: DidNotReceiveOrderReason;
  rcVersion?: string | null;
  processedAt?: string | null;
};

export type InquiryTypeKey = 'Help' | 'Return' | 'Warranty' | 'Cancellation';

export const CustomerRequestTypesArray = [
  'Return Policy',
  'Warranty',
  'Reebelo Care',
  'Repair Request',
  'Other Inquiries',
  'Vendor Support',
  'Freight Protection - Damaged in Transit',
  'Freight Protection - Lost in Transit',
];
export type CustomerRequestTypes = typeof CustomerRequestTypesArray[number];
export type CreateGeneralZendeskTicketInput = {
  email: string;
  subject: string;
  description: string;
  type: CustomerRequestTypes;
  orderId?: string;
  customerName: string;
  customerPhone?: string;
  uploads?: Array<string>;
};
export const CustomerRequestTypeMapping: Record<CustomerRequestTypes, string> =
  {
    'Return Policy': '14_days_return_policy',
    Warranty: 'warranty.',
    'Reebelo Care': 'reebelo_care',
    'Repair Request': 'repair_request',
    'Other Inquiries': 'other_inquiries',
    'Vendor Support': 'vendor_support',
    'Freight Protection - Damaged in Transit':
      'freight_protection_damaged_in_transit',
    'Freight Protection - Lost in Transit':
      'freight_protection_lost_in_transit',
    Cancellation: 'cancellation',
  };

export enum WarrantyType {
  'reebelocare' = 'warranty_reebelocare',
  'standard' = 'warranty_standard',
}

export const customerRequestTypes = {
  return: 'Return Policy',
  help: 'Other Inquiries',
  warranty: 'Warranty',
  cancellation: 'Cancellation',
};

export const resolutionMapping = {
  refund: 'Refund',
  repairAndReplace: 'Repair/Replacement',
};

export const whatHappenedMappings = {
  'WH-1': 'It was dropped / broken by me / someone else',
  'WH-2': 'It was exposed to water / humidity by me / someone else',
  'WH-3': 'Someone other than the seller tried to repair it',
  'WH-4': 'None of the above',
  'WH-5': 'Other',
};

export const whatHappenedMappingsAPAC = {
  'WH-1': 'Doesn’t work as it should or is defective',
  'WH-2': 'Damaged due to an impact or force',
  'WH-3': 'Damaged due to water exposure',
  'WH-4': "I'm looking for the nearest repair center",
  'WH-5': 'Other',
};

export const reasonForReturnMappings = {
  'RFR-1': 'Received the wrong product',
  'RFR-2': 'Product condition is different than described',
  'RFR-3': "Doesn't work or is functionally defective",
  'RFR-4': 'Problem with the battery',
  'RFR-5': 'Product arrived damaged',
  'RFR-6': 'Both Product and Package arrived damaged',
  'RFR-7': 'Missing or wrong accessories/parts',
  'RFR-8': 'I changed my mind',
  'RFR-9': 'Other',
  // Reason for Claim
  'RFC-1': "Doesn't work or is functionally defective",
  'RFC-2': 'Problem with the battery',
  'RFC-3': 'Other',
  'RFC-4': 'I would like to cancel my ReebeloCare Plan',
  'RFC-5': 'My device is damaged due to power surge',
};

export const reasonForChangeOfMindMappings = {
  'RFCM-1': 'Delivery takes too long',
  'RFCM-2': 'I found a better deal somewhere else',
  'RFCM-3': "I don't need this anymore",
  'RFCM-4': 'Item is too big / small',
  'RFCM-5': 'I would like to change / upgrade to a different model',
  'RFCM-6': 'I ordered the wrong model',
  'RFCM-7': 'I cannot afford it anymore',
  'RFCM-9': 'Unsatisfied with the condition',
  'RFCM-10': 'I entered the wrong address',
};

export type ProblemCategory =
  | 'Other'
  | 'Wrong Product'
  | 'Damaged Parcel'
  | 'Missing Item'
  | 'Change of Mind'
  | 'Quality Issue'
  | null;

export const getProblemCategory = (
  reason: string | undefined,
): ProblemCategory => {
  if (!reason) return null;
  if (
    [
      'RFR-1', // 'Received the wrong product',
    ].includes(reason)
  )
    return 'Wrong Product';

  if (
    [
      'RFR-5', // 'Product arrived damaged',
      'RFR-6', // 'Both Product and Package arrived damaged',
    ].includes(reason)
  )
    return 'Damaged Parcel';

  if (
    [
      'RFR-7', // 'Missing or wrong accessories/parts',
    ].includes(reason)
  )
    return 'Missing Item';

  if (
    [
      'RFR-8', // 'I changed my mind',
    ].includes(reason)
  )
    return 'Change of Mind';

  if (
    [
      'RFR-4', // 'Problem with the battery',
      'RFR-2', // 'Product condition is different than described',
      'RFC-1', // "Doesn't work or is functionally defective",
      'RFR-3', // "Doesn't work or is functionally defective",
      'RFC-2', // 'Problem with the battery',
    ].includes(reason)
  )
    return 'Quality Issue';

  if (
    [
      'RFR-9', // 'Other',
      'RFC-3', // 'Other',
      'RFC-5', // My device is damaged due to power surge
    ].includes(reason)
  )
    return 'Other';

  return null;
};

export const helpCategoryMapping = {
  'HPC-1': 'Did not receive order',
  'HPC-2': 'Change Address',
  'HPC-3': 'Invoice',
  'HPC-4': 'Pre-ship Cancellation',
  'HPC-5': 'Other',
};

export type ReturnLabelRecord = {
  lineItemId: string;
  // Return label creation might be initiated but not yet completed with a URL
  url?: string;
};
